import "./FileSearchModal.scss";
import { ListFilesForDashboard, ManageUser, Team } from "../data/queuedSdk";
import { useYsentinel } from "../data/YsentinelDataContext";
import { rangeWindow } from "../data/defaults";
import { Loader } from "./_shared/primitives";
import { useEffect, useState } from "react";

// UX modeled on AdvancedSearchModal — sidebar filters + results list.
// Placeholder only: filters the sample FILES array in-memory.

const TYPE_OPTIONS = ["doc", "sheet", "pdf", "image", "video", "audio", "csv", "figma", "archive"];

const ICON_BY_TYPE = {
  folder: "📁", doc: "📝", sheet: "📊", pdf: "📕",
  image: "🖼", video: "🎬", audio: "🔊", csv: "📋",
  figma: "🎨", archive: "📦",
};
const iconFor = (type) => ICON_BY_TYPE[type] || "📄";

const SIZE_UNITS = [
  { label: "B", mult: 1 },
  { label: "KB", mult: 1024 },
  { label: "MB", mult: 1024 * 1024 },
  { label: "GB", mult: 1024 * 1024 * 1024 },
];

// `variant` controls overlay scope:
//   "viewport" — full-screen modal (legacy default)
//   "panel"    — fills the nearest positioned ancestor (the Files dashboard
//                container). Sidebar / topbar stay live.
const FileSearchModal = ({ isOpen, onClose, onSelectFile, variant = "viewport", dismissible = true }) => {
  const { range } = useYsentinel();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [typeFilters, setTypeFilters] = useState([]);

  // New: size range (value + unit)
  const [sizeMin, setSizeMin] = useState("");
  const [sizeMinUnit, setSizeMinUnit] = useState(SIZE_UNITS[2].mult); // MB
  const [sizeMax, setSizeMax] = useState("");
  const [sizeMaxUnit, setSizeMaxUnit] = useState(SIZE_UNITS[2].mult); // MB

  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);

  useEffect(() => {
    ManageUser.listUsers().then(u => setAllUsers(u)).catch(() => { });
    Team.listTeams().then(t => setAllTeams(t)).catch(() => { });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    const { start, end } = rangeWindow(range);
    const timeout = setTimeout(() => {
      setLoading(true);
      ListFilesForDashboard({
        startTime: start,
        endTime: end,
        query: searchName,
        ownerEmail: userFilter,
        sharedTeam: teamFilter,
        // fileTypes: typeFilters, // disabled with the file-type chips above
        minSizeMb: sizeMin ? parseFloat(sizeMin) * (sizeMinUnit / (1024 * 1024)) : 0,
        maxSizeMb: sizeMax ? parseFloat(sizeMax) * (sizeMaxUnit / (1024 * 1024)) : 0,
      })
        .then(res => active && setResults(res))
        .catch(() => active && setResults([]))
        .finally(() => active && setLoading(false));
    }, 2000);

    return () => { active = false; clearTimeout(timeout); };
  }, [isOpen, searchName, userFilter, teamFilter, typeFilters, sizeMin, sizeMinUnit, sizeMax, sizeMaxUnit, range]);

  const toggleType = (t) =>
    setTypeFilters((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const clearAll = () => {
    setSearchName("");
    setUserFilter("");
    setTeamFilter("");
    setTypeFilters([]);
    setSizeMin(""); setSizeMax("");
  };

  const hasFilter =
    searchName || userFilter || teamFilter || typeFilters.length ||
    sizeMin || sizeMax;

  if (!isOpen) return null;

  return (
    <div
      className={`ysd-search-overlay ysd-search-overlay--${variant}`}
      onClick={dismissible ? onClose : undefined}
    >
      <div className="ysd-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ysd-search-header">
          <div className="ysd-search-header-left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <span className="ysd-search-title">Find a file</span>
            <span className="ysd-search-hint">live · backend</span>
          </div>
          {dismissible && (
            <button className="ysd-search-close" onClick={onClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18" /><path d="M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="ysd-search-body">
          <div className="ysd-search-sidebar">
            <div className="ysd-filter-group">
              <label className="ysd-filter-label">Name</label>
              <input
                className="ysd-filter-input"
                placeholder="Search by path, owner, tag…"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="ysd-filter-group">
              <label className="ysd-filter-label">Owner / shared user</label>
              <select
                className="ysd-filter-input"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
              >
                <option value="">Any user</option>
                {allUsers.map((u) => (
                  <option key={u.email} value={u.email}>
                    {u.name || u.email.split("@")[0]} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            {/* <div className="ysd-filter-group">
              <label className="ysd-filter-label">Shared with team</label>
              <select
                className="ysd-filter-input"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
              >
                <option value="">Any team</option>
                {allTeams.map((t) => {
                  const name = t.getName?.() || t.name;
                  return (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  );
                })}
              </select>
            </div> */}


            {/* File type filter — temporarily disabled, backend matching unreliable.
            <div className="ysd-filter-group">
              <label className="ysd-filter-label">File type</label>
              <div className="ysd-filter-chips">
                {TYPE_OPTIONS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`ysd-filter-chip ${typeFilters.includes(t) ? "active" : ""}`}
                    onClick={() => toggleType(t)}
                  >
                    <span>{iconFor(t)}</span>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            */}

            <div className="ysd-filter-group">
              <label className="ysd-filter-label">Size</label>
              <div className="ysd-range-row" style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "8px" }}>
                <div style={{ display: "flex", gap: "6px" }}>
                  <input
                    type="number" min="0"
                    className="ysd-filter-input ysd-filter-input--num"
                    style={{ flex: 1 }}
                    placeholder="Min size"
                    value={sizeMin}
                    onChange={(e) => setSizeMin(e.target.value)}
                  />
                  <select
                    className="ysd-filter-input ysd-filter-input--unit"
                    value={sizeMinUnit}
                    onChange={(e) => setSizeMinUnit(Number(e.target.value))}
                  >
                    {SIZE_UNITS.map((u) => (
                      <option key={u.label} value={u.mult}>{u.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <input
                    type="number" min="0"
                    className="ysd-filter-input ysd-filter-input--num"
                    style={{ flex: 1 }}
                    placeholder="Max size"
                    value={sizeMax}
                    onChange={(e) => setSizeMax(e.target.value)}
                  />
                  <select
                    className="ysd-filter-input ysd-filter-input--unit"
                    value={sizeMaxUnit}
                    onChange={(e) => setSizeMaxUnit(Number(e.target.value))}
                  >
                    {SIZE_UNITS.map((u) => (
                      <option key={u.label} value={u.mult}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="ysd-filter-actions">
              <button
                type="button"
                className="ysd-filter-btn-clear"
                onClick={clearAll}
                disabled={!hasFilter}
              >
                Clear
              </button>
              <span className="ysd-filter-count">{results.length} match{results.length === 1 ? "" : "es"}</span>
            </div>
          </div>

          <div className="ysd-search-results">
            <div className="ysd-results-list">
              {loading && <Loader overlay />}
              {results.length === 0 ? (
                <div className="ysd-results-empty">
                  {loading ? "Searching..." : "No files match your filters."}
                </div>
              ) : (
                results.map((f, i) => (
                  <div
                    key={f.uuid || i}
                    className="ysd-result-row"
                    onClick={() => {
                      onSelectFile(f.uuid || f.path);
                      onClose?.();
                    }}
                    role="button"
                  >
                    <span className="ysd-result-icon">{iconFor(f.fileType || (f.isFolder ? "folder" : "doc"))}</span>
                    <div className="ysd-result-detail">
                      <span className="ysd-result-name" title={f.path}>
                        {f.name || f.path.split("/").pop()}
                      </span>
                      <div className="ysd-result-meta">
                        <span className="ysd-result-path" title={f.path}>{f.path}</span>
                        {f.sizeMb > 0 && <span>{f.sizeMb.toFixed(2)} MB</span>}
                        <span>by {f.ownerEmail || "system"}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileSearchModal;
